import { FC, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Stack, useTheme, useMediaQuery, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { setToastMessage } from '@stores/App/slice';
import { useLazyGetPortfolioDetailsQuery } from '@api/selfServe';
import { SelfServeDataAccessStatus, TOAST_TYPES } from '@constants/constants';
import { useDeletePortfolioMutation, useToggleArchiveMutation } from '@api/selfServe';
import { useSelfServeStatusUpdate } from '@hooks/api/useSelfServeStatusUpdate';
import { UpdateSelfServeRequestStatusParams } from '../ModifyPermissions/types';
import RequestStatusSelector from '../ModifyPermissions/components/RequestStatusSelector';
import DeleteArchiveSection from '../ModifyPermissions/components/DeleteArchiveSection';
import UserDashboardsSection from '../ModifyPermissions/components/UserDashboardsSection';
import DrawerComponent from '../../../../components/Drawer/Drawer';
import RequestDetailsCard from '../components/Portfolio/RequestDetailsCard';
import GamesDetailsCard from '../components/Portfolio/GamesDetailsCard';

interface PortfolioDetailsDrawerProps {
  show: boolean;
  close: (change: boolean, reset: boolean) => void;
  portfolioId: string;
}

const statusForShowingDashboardsSelector = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.DATA_FETCH_SUCCESSFUL,
  SelfServeDataAccessStatus.PUBLISHED,
  SelfServeDataAccessStatus.REOPEN,
  SelfServeDataAccessStatus.DASHBOARD_CREATION_FAILED,
]);

const PortfolioDetailsDrawer: FC<PortfolioDetailsDrawerProps> = ({ show, close, portfolioId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const [change, setChange] = useState<boolean>(false);
  const [requestStatus, setRequestStatus] = useState<SelfServeDataAccessStatus | null>(null);
  const [userDashboards, setUserDashboards] = useState<any[]>([]);

  const [getPortfolioDetails, { data: portfolioDetails, isLoading: isPortfolioDetailsLoading }] =
    useLazyGetPortfolioDetailsQuery();

  const [toggleArchiveApi, { isLoading: togglingArchive }] = useToggleArchiveMutation();
  const [deletePortfolio, { isLoading: isDeletePortfolioLoading }] = useDeletePortfolioMutation();
  const { updateSelfServeRequestStatus: updateSelfServeRequestStatusApi, isUpdatingStatus } =
    useSelfServeStatusUpdate();

  const selfServeData = portfolioDetails?.data?.selfServe;
  const gameData = portfolioDetails?.data?.game;

  const [archived, setArchived] = useState<boolean>(selfServeData?.archived);

  const fetchPortfolioDetails = () => {
    getPortfolioDetails(portfolioId);
  };

  const updateSelfServeRequestStatus = async ({
    status,
    dashboardIds,
  }: UpdateSelfServeRequestStatusParams) => {
    if (!portfolioId) return;

    const data = await updateSelfServeRequestStatusApi({
      id: portfolioId,
      status,
      dashboardIds,
    });

    if (data) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.SUCCESS,
          heading: 'Request Status Updated',
          message: 'Request status updated successfully',
        }),
      );
      fetchPortfolioDetails();
    }
  };

  const handlePublish = async (dashboardIds: string[]) => {
    try {
      // Temporarily sending requested dashboards as empty array. Will be changed in the future.
      await updateSelfServeRequestStatus({
        status: SelfServeDataAccessStatus.PUBLISHED,
        dashboardIds,
      });
      setChange(true);
      fetchPortfolioDetails();
    } catch {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          heading: 'Publish Failed',
          message: 'Unable to publish request, please try again later.',
        }),
      );
    }
  };

  const handleReject = async () => {
    try {
      await deletePortfolio(portfolioId);
      close(change, true);
    } catch {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          heading: 'Reject of Self Serve Failed',
          message: 'Unable to reject request, please try again later.',
        }),
      );
    }
  };

  const handleArchive = async () => {
    try {
      await toggleArchiveApi(portfolioId).unwrap();
      setArchived(!archived);
      setChange(true);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          heading: 'Archive Failed',
          message: error?.error?.message,
        }),
      );
    }
  };

  useEffect(() => {
    fetchPortfolioDetails();
  }, [portfolioId]);

  useEffect(() => {
    setRequestStatus(selfServeData?.status);
    setUserDashboards(selfServeData?.dashboards || []);
  }, [selfServeData]);

  return (
    <DrawerComponent
      open={show}
      anchor="right"
      close={() => {
        close(change, false);
      }}
      height="98vh"
      zIndex={9999}
      backgroundColor="#F0F0F1"
    >
      <Stack
        width={'450px'}
        maxWidth={isMobile ? 'min(420px, 80vw)' : '450px'}
        height={'100%'}
        padding={'1.5rem 1rem'}
        gap={'1rem'}
      >
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'} gap={'0.75rem'}>
            <Typography
              fontSize={'1.375rem'}
              lineHeight={'1.875rem'}
              fontWeight={700}
              color={'#6C6C6C'}
            >
              Modify Permissions
            </Typography>
          </Stack>
          <Stack>
            <IconButton
              onClick={() => close(change, false)}
              disableFocusRipple
              disableRipple
              disableTouchRipple
            >
              <CloseIcon
                sx={{
                  color: '#162C36',
                }}
              />
            </IconButton>
          </Stack>
        </Stack>
        <RequestDetailsCard selfServeData={selfServeData} isLoading={isPortfolioDetailsLoading} />
        <GamesDetailsCard gameData={gameData} />
        {requestStatus && (
          <RequestStatusSelector
            mmp={selfServeData?.mmp}
            updateSelfServeRequestStatus={updateSelfServeRequestStatus}
            requestStatus={requestStatus}
            isUpdatingStatus={isUpdatingStatus}
          />
        )}
        {requestStatus && statusForShowingDashboardsSelector.has(requestStatus || '') && (
          <UserDashboardsSection
            requestStatus={requestStatus}
            mmp={selfServeData?.mmp || ''}
            onPublish={handlePublish}
            dashboards={userDashboards}
          />
        )}
        <DeleteArchiveSection
          isArchived={archived}
          isDeleting={isDeletePortfolioLoading}
          isTogglingArchive={togglingArchive}
          onDelete={handleReject}
          onArchive={handleArchive}
          requestStatus={requestStatus}
        />
      </Stack>
    </DrawerComponent>
  );
};

export default PortfolioDetailsDrawer;
